'use strict';

// external imports
import React, {
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';

// frontend imports
import {
    getItemTypeDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';


export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

type Props = {
    uid : string;

    formKey : string;
    formName : string;

    rootDataObjectSchema? : Schema;
    rootDataObjectItemType? : string;

    style? : CssStyle;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = DefaultProps & Props;


/**
 * Headerbar
 */
const Headerbar = ( props : AllProps ) => {
    const dispatch : AppDispatch = useDispatch(),
          {
              formKey,
              formName,
              rootDataObjectItemType,
              rootDataObjectSchema,
              style
          }                      = props,
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          language               = user
              ? user.language
              : undefined,
          itemType               = useMemo(
              () => {
                  if ( rootDataObjectSchema && rootDataObjectItemType ) {
                      return metaModel[ rootDataObjectSchema ].getItemType( rootDataObjectItemType );
                  }
                  else {
                      return undefined;
                  }
              },
              [
                  rootDataObjectItemType,
                  rootDataObjectSchema
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="title"
                    className="yn-label yn-label-value"
                >
                    {__( 'YN.base.frontend.formDesigner.header.title' )}
                </label>
            } );

            if ( layoutComponents.left.length ) {
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                } );
            }

            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="formItemLabel"
                    className="yn-label yn-label-key mr-2"
                >
                    {__( 'YN.base.frontend.formDesigner.header.form.label' )}
                </label>
            } );
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="formItem"
                    className="yn-label yn-label-value"
                >
                    {metaModel[ 'CFG' ].getItemType( 'Form' )?.displayAttribute === 'key' ? formKey : formName}
                </label>
            } );

            if ( itemType ) {
                if ( layoutComponents.left.length ) {
                    layoutComponents.left.push( {
                        type  : 'genericNode',
                        params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                    } );
                }

                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="itemType"
                        className="yn-label yn-label-key mr-2"
                    >
                        {__( 'YN.base.frontend.formDesigner.header.itemType.label' )}
                    </label>
                } );
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="rootDataObjectItemType"
                        className="yn-label yn-label-value"
                    >
                        {getItemTypeDisplayName( itemType.id, rootDataObjectSchema )}
                    </label>
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-headerbar yn-toolbar-horizontal-separator-bottom"
                style={{
                    zIndex: 6,
                    ...style
                }}
            />;
        },
        [
            itemType,
            language,
            formKey,
            formName,
            rootDataObjectSchema
        ]
    );
};

Headerbar.defaultProps = defaultProps;
Headerbar.displayName = 'FormDesigner-Headerbar';

export default Headerbar;
